/** Data access for fault reports / service requests. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import {
  compactParams,
  type ListState,
  type Paged,
  type PageOf,
  usePagedQuery,
  usePageQuery,
} from "@/shared/lib/paging";

/** Request row. */
export type ServiceRequest = Schemas["RequestOut"];
/** Request with history + transitions. */
export type ServiceRequestDetail = Schemas["RequestDetailOut"];
/** Create payload. */
export type RequestIn = Schemas["RequestIn"];
/** Transition payload. */
export type RequestTransitionIn = Schemas["RequestTransitionIn"];
/** Work order returned by convert. */
export type ConvertedWorkOrder = Schemas["WorkOrderDetailOut"];

/** Enumerations. */
export const REQUEST_CATEGORIES = [
  "fault",
  "inspection_request",
  "service_request",
  "incident",
  "observation",
] as const;
export const REQUEST_SEVERITIES = ["low", "medium", "high", "critical"] as const;
export const REQUEST_STATUSES = [
  "new",
  "acknowledged",
  "in_review",
  "converted",
  "in_progress",
  "resolved",
  "closed",
  "rejected",
] as const;

/** Filters. */
export interface RequestFilters {
  q?: string;
  status?: string;
  open_only?: boolean;
  severity?: string;
  mine?: boolean;
  asset_id?: string;
  category?: string;
  site_id?: string;
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id?: string): void {
  void qc.invalidateQueries({ queryKey: ["requests"] });
  void qc.invalidateQueries({ queryKey: ["dashboard"] });
  if (id) {
    void qc.invalidateQueries({ queryKey: ["request", id] });
  }
}

/** Paged request list. */
export function useRequests(filters: RequestFilters): Paged<ServiceRequest> {
  return usePagedQuery(["requests", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/requests/", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Sorted, numbered page of requests for the list table. */
export function useRequestPage(filters: RequestFilters, state: ListState): PageOf<ServiceRequest> {
  return usePageQuery(["requests", "page", filters], state, async (params) =>
    unwrap(
      await api.GET("/api/v1/requests/", {
        params: { query: compactParams({ ...filters, ...params }) },
      }),
    ),
  );
}

/** Single request. */
export function useRequest(id: string): UseQueryResult<ServiceRequestDetail> {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ["request", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/requests/{request_id}", { params: { path: { request_id: id } } }),
      ),
  });
}

/** Create a request. */
export function useCreateRequest(): UseMutationResult<ServiceRequestDetail, Error, RequestIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/requests/", { body })),
    onSuccess: () => invalidate(qc),
  });
}

/** Apply a transition. */
export function useRequestTransition(
  id: string,
): UseMutationResult<ServiceRequestDetail, Error, { name: string; body: RequestTransitionIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, body }) =>
      unwrap(
        await api.POST("/api/v1/requests/{request_id}/transitions/{name}", {
          params: { path: { request_id: id, name } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, id),
  });
}

/** Convert to a work order. */
export function useConvertRequest(
  id: string,
): UseMutationResult<ConvertedWorkOrder, Error, Schemas["ConvertIn"] | undefined> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap<ConvertedWorkOrder>(
        await api.POST("/api/v1/requests/{request_id}/convert", {
          params: { path: { request_id: id } },
          body: body ?? {},
        }),
      ),
    onSuccess: () => {
      invalidate(qc, id);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}

/** Make a distinct repair child without reusing the first-conversion endpoint. */
export function useFollowUpRepair(
  requestId: string,
  inspectionId: string,
): UseMutationResult<ConvertedWorkOrder, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/requests/{request_id}/follow-up/{inspection_id}", {
          params: { path: { request_id: requestId, inspection_id: inspectionId } },
          body: {},
        }),
      ),
    onSuccess: () => {
      invalidate(qc, requestId);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}
